import { supabase } from '../supabase';
import { Tables } from '@/types/database.types';
import { handleError } from './db-utils';
import { getProjectById } from './projects';

interface CreateMilestoneOptions {
  name: string;
  description?: string;
  startDate?: string | null;
  dueDate?: string | null;
  status?: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  ownerId?: string;
  ownerName?: string | null;
}

interface UpdateMilestoneOptions {
  name?: string;
  description?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  status?: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  ownerId?: string | null;
  ownerName?: string | null;
}

/**
 * Get all milestones for a project
 */
export const getProjectMilestones = async (projectId: string): Promise<Tables<'milestones'>[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check project access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    // Order by start_date first so the gantt reads left-to-right by
    // when work actually began. Due-date is the secondary key for
    // milestones that share a start. Nulls last so a milestone missing
    // both dates trails behind everything that has a date.
    const { data, error } = await supabase
      .from('milestones')
      .select('*')
      .eq('project_id', projectId)
      .order('start_date', { ascending: true, nullsFirst: false })
      .order('due_date', { ascending: true, nullsFirst: false });

    if (error) handleError(error, 'fetch milestones');
    return data || [];
  } catch (error) {
    handleError(error, 'fetch milestones');
    return [];
  }
};

/**
 * Create a new milestone for a project
 */
export const createMilestone = async (
  projectId: string,
  options: CreateMilestoneOptions
): Promise<Tables<'milestones'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check project access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('milestones')
      .insert({
        project_id: projectId,
        name: options.name,
        description: options.description || null,
        start_date: options.startDate || null,
        due_date: options.dueDate || null,
        status: options.status || 'not_started',
        owner_id: options.ownerId || null,
        owner_name: options.ownerName || null,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) handleError(error, 'create milestone');
    if (!data) throw new Error('No data returned from create');
    return data;
  } catch (error) {
    handleError(error, 'create milestone');
    throw error;
  }
};

/**
 * Update a milestone
 */
export const updateMilestone = async (
  milestoneId: string,
  options: UpdateMilestoneOptions
): Promise<Tables<'milestones'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (options.name !== undefined) updateData.name = options.name;
    if (options.description !== undefined) updateData.description = options.description;
    if (options.startDate !== undefined) updateData.start_date = options.startDate;
    if (options.dueDate !== undefined) updateData.due_date = options.dueDate;
    if (options.status !== undefined) updateData.status = options.status;
    if (options.ownerId !== undefined) updateData.owner_id = options.ownerId;
    if (options.ownerName !== undefined) updateData.owner_name = options.ownerName;

    const { data, error } = await supabase
      .from('milestones')
      .update(updateData)
      .eq('id', milestoneId)
      .select()
      .single();

    if (error) handleError(error, 'update milestone');
    if (!data) throw new Error('No data returned from update');
    return data;
  } catch (error) {
    handleError(error, 'update milestone');
    throw error;
  }
};

/**
 * Delete a milestone
 */
export const deleteMilestone = async (milestoneId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('milestones')
      .delete()
      .eq('id', milestoneId);

    if (error) handleError(error, 'delete milestone');
  } catch (error) {
    handleError(error, 'delete milestone');
    throw error;
  }
};
